import { useContext, useCallback, useEffect } from 'react'
import { UIGeneralContextType, UIGeneralContext } from '../contextProviders/uiGeneralContext'

export const useGroupedHover = <T extends HTMLElement>(
  groupId: string | number,
  sectionId: string | number
): [(node: T) => void, boolean] => {
  const context = useContext<UIGeneralContextType>(UIGeneralContext)
  if (null == context) {
    throw new Error('useGroupedHover must be used within a <UIGeneralProvider>')
  }
  const { hoverHandlers, hoverState, updateHoverState } = context

  const group = hoverHandlers[groupId] || Object.assign(hoverHandlers, { [groupId]: {} })[groupId]
  const section = group[sectionId] || Object.assign(group, { [sectionId]: {} })[sectionId]

  const handleMouseEnter = useCallback(() => {
    updateHoverState(state => (state[groupId] ? state : { ...state, [groupId]: true }))
  }, [updateHoverState, groupId])

  const handleMouseLeave = useCallback(() => {
    updateHoverState(state => (!state[groupId] ? state : { ...state, [groupId]: false }))
  }, [updateHoverState, groupId])

  const customRef = useCallback(
    (node: T) => {
      if (section.node?.nodeType === Node.ELEMENT_NODE) {
        section.node.removeEventListener('mouseenter', handleMouseEnter)
        section.node.removeEventListener('mouseleave', handleMouseLeave)
      }
      if (node?.nodeType === Node.ELEMENT_NODE) {
        node.addEventListener('mouseenter', handleMouseEnter)
        node.addEventListener('mouseleave', handleMouseLeave)
      }
      section.node = node
    },
    [section, handleMouseEnter, handleMouseLeave]
  )

  useEffect(
    () => () => {
      delete group[sectionId]
      if (0 === Object.keys(group).length) {
        delete hoverHandlers[groupId]
      }
    },
    [group, sectionId, hoverHandlers, groupId]
  )

  return [customRef, hoverState[groupId] ?? false]
}
